const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const FEE_COLUMN_PRIORITY = [
  "admission",
  "security",
  "monthly",
  "academic",
];


export function feeRevenueMonthLabel(month, year) {
  const monthName = MONTH_NAMES[Number(month) - 1] || "";
  return [monthName, year].filter(Boolean).join(" ");
}

export function feeRevenueDateRange(month, year) {
  const m = Number(month) || 1;
  const y = Number(year) || new Date().getFullYear();
  const lastDay = new Date(y, m, 0).getDate();
  const monthPad = String(m).padStart(2, "0");
  return {
    month: m,
    year: y,
    dtFrom: `${y}-${monthPad}-01`,
    dtTo: `${y}-${monthPad}-${String(lastDay).padStart(2, "0")}`,
  };
}

export function toFeeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function formatFeeAmount(value) {
  const n = toFeeNumber(value);
  if (!n) return "-";
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatFeeCount(value) {
  const n = toFeeNumber(value);
  if (!n) return "-";
  return n.toLocaleString("en-US");
}

function feePriority(title) {
  const t = String(title || "").toLowerCase();
  const index = FEE_COLUMN_PRIORITY.findIndex((key) => t.includes(key));
  return index === -1 ? FEE_COLUMN_PRIORITY.length : index;
}

export function collectFeeTypeColumns(data = []) {
  const titles = [];
  const seen = new Set();
  (Array.isArray(data) ? data : []).forEach((item) => {
    (item.feeTypes || []).forEach((fee) => {
      const title = String(fee.title || "").trim();
      if (!title || seen.has(title)) return;
      seen.add(title);
      titles.push(title);
    });
  });
  return titles.sort((a, b) => {
    const pa = feePriority(a);
    const pb = feePriority(b);
    if (pa !== pb) return pa - pb;
    return a.localeCompare(b);
  });
}

function feeTypeAmount(feeTypes, title) {
  const match = (Array.isArray(feeTypes) ? feeTypes : []).find(
    (fee) => String(fee.title || "").trim() === title
  );
  return toFeeNumber(match && match.revenue);
}

export function mapFeeRevenueRow(item = {}, feeTypeColumns = []) {
  const amounts = {};
  feeTypeColumns.forEach((title) => {
    amounts[title] = feeTypeAmount(item.feeTypes, title);
  });

  return {
    srNo: item.srNo,
    batchTitle: item.cls || item.batchTitle || "-",
    male: toFeeNumber(item.maleCount ?? item.male),
    female: toFeeNumber(item.femaleCount ?? item.female),
    amounts,
    totalRevenue: toFeeNumber(item.totalRevenue ?? item.total),
    totalPaid: toFeeNumber(item.totalPaid),
    totalRemaining: toFeeNumber(item.totalRemaining),
  };
}

export function buildFeeRevenueRows(data = []) {
  const feeTypeColumns = collectFeeTypeColumns(data);
  const rows = (Array.isArray(data) ? data : []).map((item) =>
    mapFeeRevenueRow(item, feeTypeColumns)
  );
  return { rows, feeTypeColumns };
}

export function buildFeeRevenueTotals(rows = [], feeTypeColumns = []) {
  const totals = {
    male: 0,
    female: 0,
    amounts: {},
    totalRevenue: 0,
    totalPaid: 0,
    totalRemaining: 0,
  };
  feeTypeColumns.forEach((title) => {
    totals.amounts[title] = 0;
  });

  rows.forEach((row) => {
    totals.male += toFeeNumber(row.male);
    totals.female += toFeeNumber(row.female);
    totals.totalRevenue += toFeeNumber(row.totalRevenue);
    totals.totalPaid += toFeeNumber(row.totalPaid);
    totals.totalRemaining += toFeeNumber(row.totalRemaining);
    feeTypeColumns.forEach((title) => {
      totals.amounts[title] += toFeeNumber(row.amounts && row.amounts[title]);
    });
  });

  return totals;
}

export function buildFeeRevenueExcelRows(data = []) {
  const { rows, feeTypeColumns } = buildFeeRevenueRows(data);
  const totals = buildFeeRevenueTotals(rows, feeTypeColumns);

  const toExcelRow = (row, index, isTotal) => {
    const excelRow = {
      "Sr#": isTotal ? "Total" : row.srNo || index + 1,
      "Batch Title": isTotal ? "" : row.batchTitle,
      Male: row.male || 0,
      Female: row.female || 0,
    };
    feeTypeColumns.forEach((title) => {
      excelRow[title] = (row.amounts && row.amounts[title]) || 0;
    });
    excelRow["Total Revenue"] = row.totalRevenue || 0;
    excelRow["Total Paid"] = row.totalPaid || 0;
    excelRow["Total Remaining"] = row.totalRemaining || 0;
    return excelRow;
  };

  const excelRows = rows.map((row, index) => toExcelRow(row, index, false));
  excelRows.push(toExcelRow(totals, 0, true));
  return excelRows;
}
